import React from "react";
import { useLocation, matchPath } from "react-router";
import { Link } from "react-router-dom";
import { useAuth } from "@syos-music/auth";

import { styled, Avatar, IconButton, Tab, Tabs, Tooltip } from "@mui/material";
import {
  Home as HomeIcon,
  Cart as OrdersIcon,
  Inbox as InventoryIcon,
  WalletGiftcard as DiscountCodeIcon,
  Receipt as BillingIcon,
  ImageText as ArticlesIcon,
  Star as SignatureMouthpiecesIcon,
  ChartBar as StatisticsIcon,
  Barcode as BarcodesIcon,
  MessageDraw as ReviewsIcon,
  BookmarkMusic as EntityTagsIcon,
  Cog as AdminIcon,
  Brightness4 as DarkIcon,
  Brightness7 as LightIcon,
} from "mdi-material-ui";

import { useThemeMode } from "lib/theme";

const NAV_ITEMS = [
  { to: "/", exact: true, title: "Accueil", label: <HomeIcon /> },
  { to: "/orders", title: "Commandes", label: <OrdersIcon /> },
  { to: "/barcodes", title: "Code-barres", label: <BarcodesIcon /> },
  { to: "/inventory", title: "Inventaire", label: <InventoryIcon /> },
  { to: "/billing", title: "Factures", label: <BillingIcon /> },
  {
    to: "/discount-codes",
    title: "Codes de réduction",
    label: <DiscountCodeIcon />,
  },
  { to: "/articles", title: "Articles", label: <ArticlesIcon /> },
  {
    to: "/signature-mouthpieces",
    title: "Becs signature",
    label: <SignatureMouthpiecesIcon />,
  },
  { to: "/reviews", title: "Avis client", label: <ReviewsIcon /> },
  { to: "/entity-tags", title: "Tags", label: <EntityTagsIcon /> },
  { to: "/stats", title: "Statistiques", label: <StatisticsIcon /> },
  { to: "/admin", title: "Admin", label: <AdminIcon /> },
].filter(Boolean);

const Nav = styled("nav")(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;

    max-width: 56px;
    min-width: 56px;
    background-color: ${theme.palette.primary.main};
    color: ${theme.palette.primary.contrastText};
  `
);

export default function AppBar({ onToggleMode, ...props }) {
  const location = useLocation();
  const { idTokenPayload, isLoggedIn, hasRole } = useAuth();
  const isSyosMember = hasRole("SYOS_MEMBER");

  const [themeMode, toggleThemeMode] = useThemeMode();

  const tabValue = React.useMemo(() => {
    if (!isSyosMember) return "null";

    const navItem = NAV_ITEMS.find(({ to, exact }) =>
      matchPath(location.pathname, { path: to, exact })
    );

    return navItem?.to ?? "null";
  }, [isSyosMember, location.pathname]);

  return (
    <Nav {...props}>
      <IconButton
        component={Link}
        to={isLoggedIn ? "/user" : "/user/login"}
        color="inherit"
      >
        <Avatar src={idTokenPayload?.picture} />
      </IconButton>

      <Tabs
        orientation="vertical"
        variant="scrollable"
        value={tabValue}
        textColor="inherit"
        TabIndicatorProps={{
          sx: { right: "initial", left: 0, bgcolor: "currentColor" },
        }}
        sx={{ flexGrow: 1 }}
      >
        <Tab value="null" sx={{ minHeight: 0, padding: 0 }} />

        {NAV_ITEMS.map((navItem) => (
          <LinkTab key={navItem.to} value={navItem.to} {...navItem} />
        ))}
      </Tabs>

      <Tooltip
        title={themeMode === "light" ? "Lapidark" : "Lapilight"}
        placement="right"
      >
        <IconButton
          onClick={toggleThemeMode}
          sx={{
            width: 56,
            height: 56,
            alignSelf: "center",
            color: "inherit",
            opacity: 0.6,
          }}
        >
          {themeMode === "light" ? <DarkIcon /> : <LightIcon />}
        </IconButton>
      </Tooltip>
    </Nav>
  );
}

function LinkTab({ title, exact, ...props }) {
  return (
    <Tooltip title={title} placement="right">
      <Tab sx={{ minWidth: 56, maxWidth: 56 }} component={Link} {...props} />
    </Tooltip>
  );
}
